import axios from 'axios';
import { PRODUCT_MODE, getProductMode } from './productMode';

/**
 * veCall — the single canonical REST transport for the cloud NVR
 * (cloudapi's `/ve` videoengine API). It is the cloud analog of
 * `jsonRpcCall.js`: one primitive every CloudNvrAdapter method routes through,
 * so the envelope (base URL, JWT header, timeout) lives in exactly one place.
 *
 * Contract differences from the local JSON-RPC transport:
 *   - REST, not JSON-RPC: method + path (e.g. GET `/ve/onlinestatus`), query
 *     params, and JSON bodies — no `{jsonrpc,method,id,params}` envelope.
 *   - Auth is a JWT bearer header (`Authorization: Bearer <token>`), not a
 *     positional sSess param. The token is supplied by the adapter, which holds
 *     it after `/ve/login`.
 *   - Cloud responses are plain JSON objects/arrays, NOT the `[ok,payload]`
 *     tuple. Callers read `res.data` directly.
 *
 * WHY the GLOBAL `axios` singleton (same reasoning as jsonRpcCall): any global
 * config/interceptors installed once at startup apply here too. The local
 * session-expiry interceptor (axiosSessionInterceptor) only fires on JSON-RPC
 * POSTs whose `result[1] === "noauth"` — cloud REST responses never match that
 * shape, so routing cloud calls through the same axios is a safe no-op for it.
 * (Cloud JWT-expiry → HTTP 401 surfaces as a normal axios rejection, handled by
 * the adapter/caller; that's a separate concern from the local noauth path.)
 *
 * Base URL: the DEPLOYED hosted CloudNVR viewer is CROSS-ORIGIN to cloudapi — the
 * prod build bakes `REACT_APP_CLOUD_API=https://api.cloud.dividia.net` and
 * CloudFront serves only the static app (no `/ve` behavior), so the browser calls
 * `api.cloud` cross-origin (same SITE, different ORIGIN; both under dividia.net).
 * The appliance "Lite Viewer" instead reaches cloudapi through its same-origin
 * `/cloudapi` Apache proxy. In dev, setupProxy forwards `/ve` same-origin. The
 * portal Microsoft OAuth binding cookie needs `withCredentials` on the cross-origin
 * authurl (to accept Set-Cookie) and status (to send the cookie) — see
 * `opts.withCredentials` below; cloudapi's global credentialed CORS already allows
 * the nvr.cloud / nvr-staging origins.
 */

const DEFAULT_TIMEOUT_MS = 10000;

/**
 * cloudapi names its build on every response (X-Cloudapi-Version, exposed through
 * CORS). The /ve/login payload's backendVersion is only the build that answered
 * the login, and a refresh restores it from localStorage, so an open tab would
 * show it until the next login. Announce the version actually answering instead.
 * Compare per tab: localStorage is shared, so another tab may already have saved
 * the new version. App.js applies the event to the cloud adapter.
 */
export const CLOUDAPI_VERSION_EVENT = 'dview:cloudapi-version';
let lastCloudApiVersion = null;
export const noteCloudApiVersion = (response) => {
  const version = response && response.headers && response.headers['x-cloudapi-version'];
  if (typeof version !== 'string' || !version || version === lastCloudApiVersion) return;
  lastCloudApiVersion = version;
  window.dispatchEvent(new CustomEvent(CLOUDAPI_VERSION_EVENT, { detail: version }));
};
/** Test seam: forget the version this tab has seen. */
export const _resetCloudApiVersionForTesting = () => { lastCloudApiVersion = null; };
const ALLOWED_RUNTIME_BASES = [
  'https://api.cloud.dividia.net',
  'https://api-staging.cloud.dividia.net',
];
let runtimeCloudApiBase = null;
let runtimeCloudApiConfigured = false;

/**
 * Apply the cloudapi origin supplied by the local NVR startup payload.
 * Unknown origins fail closed and clear any prior server selection.
 * @param {string|null} base
 * @returns {boolean} whether the selection was accepted
 */
export const setCloudApiBase = (base) => {
  if (base == null || base === '') {
    runtimeCloudApiBase = null;
    runtimeCloudApiConfigured = false;
    return true;
  }
  const normalized = String(base).replace(/\/+$/, '');
  if (!ALLOWED_RUNTIME_BASES.includes(normalized)) {
    runtimeCloudApiBase = null;
    runtimeCloudApiConfigured = true;
    return false;
  }
  runtimeCloudApiBase = normalized;
  runtimeCloudApiConfigured = true;
  return true;
};

/**
 * Resolve the cloudapi base for one deployed product.
 *
 * The hosted cloud viewer owns `/ve` on its origin. The appliance viewer does
 * not: its Apache config exposes cloudapi under `/cloudapi/`, while unknown
 * `/ve/*` paths fall through to the React SPA. Local development keeps the empty
 * base because setupProxy.js forwards `/ve` to its configured target.
 *
 * A production appliance ALWAYS routes through its same-origin `/cloudapi`
 * proxy, REGARDLESS of which allow-listed cloud endpoint its profile selected
 * (prod OR staging). The Apache `/cloudapi/` reverse proxy targets whichever
 * endpoint `nvr cloudapi-profile` chose (rendered at viewer-start from the
 * mounted profile), so the browser never calls cloudapi cross-origin. This
 * matters on the staging profile: sCloudApiUrl is api-staging.cloud.dividia.net,
 * which cloudapi CORS never allow-lists for an NVR origin, so a cross-origin
 * call is blocked by the browser and OAuth fails at fetchAuthUrl. The old
 * prod-only guard let staging fall through to that cross-origin call.
 */
export const resolveCloudApiBase = ({
  configuredBase,
  productMode,
  isProduction,
}) => {
  const base = configuredBase ? configuredBase.replace(/\/+$/, '') : '';
  if (isProduction && productMode === PRODUCT_MODE.LOCAL) {
    return '/cloudapi';
  }
  if (base) return base;
  return '';
};

/**
 * Origin for cloudapi. Empty string = same-origin (relative `/ve/...`), which
 * is what a production nvr.cloud deploy uses. The production local-NVR viewer
 * uses its same-origin `/cloudapi` reverse proxy. A dev/staging override points
 * at a remote cloudapi.
 * @returns {string} origin without a trailing slash, or '' for same-origin
 */
export const getCloudApiBase = () => {
  if (runtimeCloudApiConfigured && runtimeCloudApiBase == null) return '';
  return resolveCloudApiBase({
    configuredBase: runtimeCloudApiConfigured
      ? runtimeCloudApiBase
      : process.env.REACT_APP_CLOUD_API,
    productMode: getProductMode(),
    isProduction: process.env.NODE_ENV === 'production',
  });
};

/**
 * Build a full `/ve` endpoint URL.
 * @param {string} path - path under `/ve` (leading slash optional)
 * @returns {string}
 */
export const veUrl = (path) => {
  const p = String(path || '').startsWith('/') ? path : `/${path}`;
  return `${getCloudApiBase()}/ve${p}`;
};

/**
 * @param {string} path - endpoint path under `/ve` (e.g. 'login', '/onlinestatus')
 * @param {Object} [opts]
 * @param {string} [opts.method='get'] - HTTP method
 * @param {Object} [opts.params] - query-string params
 * @param {Object} [opts.data] - JSON request body (POST/PUT)
 * @param {string} [opts.token] - JWT; sent as `Authorization: Bearer <token>`
 * @param {number} [opts.timeout=10000] - axios timeout (ms)
 * @param {AbortSignal} [opts.signal] - cancels the request through axios 0.19's CancelToken
 * @param {boolean} [opts.withCredentials] - send/accept cookies cross-origin. Used
 *   ONLY by the portal OAuth authurl/status calls so the __Host- binding cookie
 *   round-trips; NOT set on the global axios singleton, so no other call ships cookies.
 * @returns {Promise<import('axios').AxiosResponse>} the raw axios response
 */
export const veCall = (path, opts = {}) => {
  const { method = 'get', params, data, token, timeout = DEFAULT_TIMEOUT_MS, signal, withCredentials } = opts;

  const headers = { Accept: 'application/json' };
  if (data != null) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;

  // axios 0.19 does not understand AbortSignal. Translate it to the version's
  // CancelToken API so keyed React fetches can stop obsolete network work.
  let cancelSource = null;
  let abort = null;
  if (signal && typeof axios.CancelToken !== 'undefined' && axios.CancelToken.source) {
    cancelSource = axios.CancelToken.source();
    abort = () => cancelSource.cancel('aborted');
    if (signal.aborted) abort();
    else signal.addEventListener('abort', abort, { once: true });
  }

  const request = axios({
    method,
    url: veUrl(path),
    headers,
    ...(params != null ? { params } : {}),
    ...(data != null ? { data } : {}),
    ...(cancelSource ? { cancelToken: cancelSource.token } : {}),
    ...(withCredentials ? { withCredentials: true } : {}),
    timeout,
  });
  // An error response (401, 403, 5xx) still names the build that answered it.
  const noted = request.then(
    (value) => { noteCloudApiVersion(value); return value; },
    (error) => { noteCloudApiVersion(error && error.response); throw error; },
  );
  if (!abort || !signal || typeof signal.removeEventListener !== 'function') return noted;
  return noted.then(
    (value) => { signal.removeEventListener('abort', abort); return value; },
    (error) => { signal.removeEventListener('abort', abort); throw error; },
  );
};
